import { useEffect, useState } from "react";
import { ArrowLeft, Download, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader, BrandButton } from "@/components/PageHeader";
import { EventGroup, EventCard, downloadCsv, regRow, customFieldsOf, REG_HEADERS } from "@/components/event-registrations/EventGroup";
import { useMemberForm } from "@/components/member/ProfileFields";
import { api } from "@/lib/api";
import { useApp, useCanExport } from "@/context/AppContext";

const exportAll = (events, cf) => downloadCsv("gamls-all-event-registrations.csv", ["Event", ...REG_HEADERS(cf)],
  events.flatMap((ev) => ev.registrations.map((r) => [ev.title, ...regRow(r, cf)])));

export default function EventRegistrations() {
  const [events, setEvents] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState(false);
  const [openId, setOpenId] = useState(null);
  const cf = customFieldsOf(useMemberForm());
  const { role } = useApp();
  const canExport = useCanExport();
  const canDelete = true;
  const load = () => api.get("/event-registrations").then((r) => setEvents(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); }, []);
  const list = (events || []).filter((e) => e.title.toLowerCase().includes(q.toLowerCase()));
  const current = openId && (events || []).find((e) => e.id === openId);
  const all = current ? current.registrations : (events || []).flatMap((e) => e.registrations);
  const canEdit = ["Super Admin", "Event Manager", "Accountant", "Accounts Officer", "Financial Secretary", "Registration Officer", "Admin Assistant", "Administrator", "General Secretary"].includes(role);
  return (
    <div data-testid="event-registrations-page">
      <PageHeader eyebrow="Registrations" title={current ? current.title : "Event Registrations"} description={current ? "Search, filter or export this event's registrations. Click a participant's name to see their full registration details." : "Pick an event to see everyone registered for it."}
        actions={current ? <BrandButton variant="outline" onClick={() => setOpenId(null)} data-testid="evreg-back-to-events"><ArrowLeft className="w-4 h-4" />All events</BrandButton> : canExport ? <BrandButton variant="outline" onClick={() => exportAll(events || [], cf)} disabled={!all.length} data-testid="evreg-export-all"><Download className="w-4 h-4" />Export all events</BrandButton> : null} />
      {err ? <div className="p-10 text-center text-sm text-[#E60000] bg-white border rounded-lg" data-testid="evreg-error">Could not load registrations. Please refresh the page.</div> : !events ? <div className="text-gray-500">Loading…</div> : current ? (
        <EventGroup key={current.id} ev={current} cf={cf} canDelete={canDelete} canEdit={canEdit} canPermanent={role === "Super Admin"} onChanged={load} />
      ) : (<>
        <div className="relative mb-4 max-w-md"><Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find an event" className="pl-9 h-10 bg-white" data-testid="evreg-event-search" /></div>
        <div className="space-y-3">
          {list.map((ev) => <EventCard key={ev.id} ev={ev} onOpen={() => setOpenId(ev.id)} />)}
          {list.length === 0 && <div className="p-10 text-center text-sm text-gray-500 bg-white border rounded-lg" data-testid="evreg-no-events">No events found.</div>}
        </div>
      </>)}
    </div>
  );
}
